import { differenceInCalendarDays, isFuture } from 'date-fns'
import { mixed, object, string } from 'yup'

import {
  EmploymentHistoryEquipmentType,
  EmploymentHistoryTrailerType,
  EmploymentJob,
  EmploymentJobType,
  LeavingReason,
} from '../../../../models/employment'

export type PositionForm = {
  jobType: EmploymentJobType | ''
  startDate: string
  endDate: string
  companyName: string
  companyAddress: string | undefined
  positionHeld: string
  isCurrentlyWorkingHere: boolean
  equipmentType?: EmploymentHistoryEquipmentType
  trailerType?: EmploymentHistoryTrailerType
  leavingReason?: LeavingReason
  leavingReasonDetail?: string
}

export const vehicleOptions = [
  {
    label: 'Bus',
    value: EmploymentHistoryEquipmentType.BUS,
  },
  {
    label: 'Conventional tractor',
    value: EmploymentHistoryEquipmentType.CONVENTIONAL_TRACTOR,
  },
  {
    label: 'Tractor-trailer',
    value: EmploymentHistoryEquipmentType.TRACTOR_TRAILER,
  },
  {
    label: 'Straight truck',
    value: EmploymentHistoryEquipmentType.STRAIGHT_TRUCK,
  },
]

export const trailerOptions = [
  {
    label: 'Flatbed',
    value: EmploymentHistoryTrailerType.FLATBED,
  },
  {
    label: 'Reefer',
    value: EmploymentHistoryTrailerType.REEFER,
  },
  {
    label: 'Dry van',
    value: EmploymentHistoryTrailerType.DRY_VAN,
  },
  {
    label: 'Other',
    value: EmploymentHistoryTrailerType.OTHER,
  },
]

export const leavingReasonOptions = [
  {
    label: 'Laid off',
    value: LeavingReason.LAID_OFF,
  },
  {
    label: 'Terminated',
    value: LeavingReason.TERMINATED,
  },
  {
    label: 'Personal reasons',
    value: LeavingReason.PERSONAL_REASONS,
  },
  {
    label: 'Changed jobs',
    value: LeavingReason.CHANGED_JOBS,
  },
  {
    label: 'Other',
    value: LeavingReason.OTHER,
  },
]

export const getEmploymentHistoryPositionInitialForm = (item?: EmploymentJob): PositionForm => {
  if (!item)
    return {
      jobType: '',
      startDate: '',
      endDate: '',
      companyName: '',
      companyAddress: undefined,
      positionHeld: '',
      isCurrentlyWorkingHere: false,
      equipmentType: undefined,
      trailerType: undefined,
      leavingReason: undefined,
      leavingReasonDetail: undefined,
    }

  return {
    jobType: item.jobType,
    startDate: item.details.startDateFormatted,
    endDate: item.details.endDateFormatted,
    companyName: item.details.companyName,
    positionHeld: item.details.positionHeld ?? '',
    companyAddress: item.details.companyAddress ?? '',
    isCurrentlyWorkingHere: item.details.isCurrentlyWorkingHere,
    equipmentType: item.details.equipmentType ?? undefined,
    trailerType: item.details.trailerType ?? undefined,
    leavingReason: item.details.leavingReason ?? undefined,
    leavingReasonDetail: item.details.leavingReasonDetail ?? '',
  }
}

export const getEmploymentHistoryPositionFormSchema = () =>
  object().shape({
    jobType: mixed().required('Select a job type'),
    startDate: string().test(function (val) {
      if (!val) return this.createError({ message: 'Enter the start date' })
      if (val.length !== 7 || val.includes('_') || Number(val.split('/')[0]) > 12)
        return this.createError({ message: 'Please enter a valid date' })

      // `01/${val}` was actually creating a date in January of the year, b/c date is processed as MM/DD/YYYY, -1 for month indexing starting at 0
      const startDate = new Date(parseInt(val.split('/')[1]), parseInt(val.split('/')[0]) - 1)
      const future = isFuture(startDate)
      if (future) return this.createError({ message: 'Start date must be in the past' })
      if (this.parent.isCurrentlyWorkingHere) return true

      if (this.parent.endDate && this.parent.endDate.length === 7) {
        const endDate = new Date(
          parseInt(this.parent.endDate.split('/')[1]),
          parseInt(this.parent.endDate.split('/')[0]) - 1
        )
        const diffToEnd = differenceInCalendarDays(endDate, startDate)
        if (diffToEnd < 0) {
          return this.createError({ message: 'Start date has to be earlier' })
        }
      }
      return true
    }),
    endDate: mixed().test(function (val) {
      if (this.parent.isCurrentlyWorkingHere) return true
      if (!val) return this.createError({ message: 'Enter the start date' })
      if (val.length !== 7 || val.includes('_') || Number(val.split('/')[0]) > 12)
        return this.createError({ message: 'Please enter a valid date' })

      // `01/${val}` was actually creating a date in January of the year, b/c date is processed as MM/DD/YYYY, -1 for month indexing starting at 0
      const endDate = new Date(parseInt(val.split('/')[1]), parseInt(val.split('/')[0]) - 1)
      const future = isFuture(endDate)
      if (future) return this.createError({ message: 'End date must be in the past' })
      return true
    }),
    companyName: string().required('Enter your company’s name'),
    positionHeld: mixed().test(function (val) {
      if (this.parent.jobType !== EmploymentJobType.DRIVING && !val)
        return this.createError({ message: 'Select position held' })
      return true
    }),
    companyAddress: mixed().test(function (val) {
      if (this.parent.jobType === EmploymentJobType.DRIVING && !val)
        return this.createError({ message: 'Enter your company’s address' })
      return true
    }),
    equipmentType: mixed().test(function (val) {
      if (this.parent.jobType === EmploymentJobType.DRIVING && !val)
        return this.createError({ message: 'Select equipment driven' })
      return true
    }),
    trailerType: mixed().test(function (val) {
      if (this.parent.jobType === EmploymentJobType.DRIVING && !val)
        return this.createError({ message: 'Select trailer type' })
      return true
    }),
    leavingReason: mixed().test(function (val) {
      if (this.parent.isCurrentlyWorkingHere) return true

      if (!val) return this.createError({ message: 'Select your reason for leaving' })
      return true
    }),
    leavingReasonDetail: mixed().test(function (val) {
      if (this.parent.isCurrentlyWorkingHere) return true

      if (this.parent.leavingReason === LeavingReason.OTHER && !val)
        return this.createError({ message: 'This field is required' })
      return true
    }),
  })

export const getEmploymentHistoryPositionInitialErrors = (employmentHistoryItem: EmploymentJob) => {
  const errors: Array<string> = []
  const {
    jobType,
    details: {
      startDate,
      companyName,
      companyAddress,
      positionHeld,
      isCurrentlyWorkingHere,
      equipmentType,
      trailerType,
      leavingReason,
      leavingReasonDetail,
    },
  } = employmentHistoryItem

  if (!jobType) errors.push(`Job type`)
  if (!startDate) errors.push(`Start date`)
  if (!companyName) errors.push(`Company`)
  if (jobType === EmploymentJobType.DRIVING) {
    if (!companyAddress) errors.push(`City, state`)
    if (!equipmentType) errors.push(`Vehicle`)
    if (!trailerType) errors.push(`Trailer`)
    if (!isCurrentlyWorkingHere && !leavingReason) errors.push(`Leaving reason`)
    if (!isCurrentlyWorkingHere && leavingReason === LeavingReason.OTHER && !leavingReasonDetail)
      errors.push(`Leaving reason short explanation`)
  } else {
    if (!positionHeld) errors.push(`Job title`)
  }

  return errors
}
